import { create } from 'zustand';

import { ATTRIBUTION_KEYS } from 'constants/attribution';
import { useAuthStore } from 'lib/stores/authStore';
import { addUserAttributionTouchpoint } from 'transport/domains/profile';
import { getOrCreateLoggedOutUserId, getStoredCid } from 'utils/analytics/analyticsProvider';
import { addAttributionEntry, getAttributionHistory, isWithinAttributionTouchpointPostWindow } from 'utils/attribution';
import interactionResponse from 'utils/interaction-response';
import { getLocalStorageWithExpiration } from 'utils/storage';
import { uuidv4 } from 'utils/uuid';

export interface UTM {
    utm_content: string | undefined;
    utm_source: string | undefined;
    utm_campaign: string | undefined;
    utm_term: string | undefined;
    utm_medium: string | undefined;
    utm_landing?: string | undefined;
}

export interface AttributionHistoryEntry extends UTM {
    timestamp: string;
    event_id?: string;
    channel?: string;
    gclid?: string | null;
    fbclid?: string | null;
    msclkid?: string | null;
    dclid?: string | null;
    rdt_cid?: string | null;
    ttclid?: string | null;
    twclid?: string | null;
    sc_cid?: string | null;
    cid?: string | null;
    anonymous_id?: string | null;
    initial_referrer?: string | null;
}

const defaultUTM: UTM = {
    utm_content: undefined,
    utm_source: undefined,
    utm_campaign: undefined,
    utm_term: undefined,
    utm_medium: undefined,
    utm_landing: undefined,
};

interface AttributionState {
    utmData: UTM;
    source: string | undefined;
    actions: {
        setUTMData: (utm: UTM) => void;
        setSource: (source: string | undefined) => void;
    };
}

async function trackUtmHistory(utm: UTM): Promise<void> {
    if (typeof window === 'undefined') return;
    const hasCampaignData =
        (utm.utm_source && utm.utm_source !== '') ||
        (utm.utm_medium && utm.utm_medium !== '') ||
        (utm.utm_campaign && utm.utm_campaign !== '');

    const history = getAttributionHistory();

    // Read click IDs from localStorage before the early-return gate so that
    // click-ID-only arrivals (no UTM params) are not dropped when history exists.
    const gclid = getLocalStorageWithExpiration(ATTRIBUTION_KEYS.GCLID) ?? null;
    const fbclid = getLocalStorageWithExpiration(ATTRIBUTION_KEYS.FBCLID) ?? null;
    const msclkid = getLocalStorageWithExpiration(ATTRIBUTION_KEYS.MSCLKID) ?? null;
    const dclid = getLocalStorageWithExpiration(ATTRIBUTION_KEYS.DCLID) ?? null;
    const rdt_cid = getLocalStorageWithExpiration(ATTRIBUTION_KEYS.RDT_CID) ?? null;
    const ttclid = getLocalStorageWithExpiration(ATTRIBUTION_KEYS.TTCLID) ?? null;
    const twclid = getLocalStorageWithExpiration(ATTRIBUTION_KEYS.TWCLID) ?? null;
    const sc_cid = getLocalStorageWithExpiration(ATTRIBUTION_KEYS.SC_CID) ?? null;

    // A touchpoint is also meaningful when click IDs are present even without UTM params
    const hasClickIds = !!(gclid || fbclid || msclkid || dclid || rdt_cid || ttclid || twclid || sc_cid);

    // Skip internal navigation unless it has campaign data or click IDs
    if (!hasCampaignData && !hasClickIds) {
        // Only track the initial landing page (first touchpoint)
        if (history.length > 0) {
            return; // Skip internal navigation after initial landing
        }
    }

    try {
        // Read cid and anonymous_id
        const cid = getStoredCid() ?? null;
        const anonymous_id = getOrCreateLoggedOutUserId() ?? null;

        // Read initial referrer from sessionStorage
        let initial_referrer: string | null = null;
        if (typeof sessionStorage !== 'undefined') {
            initial_referrer = sessionStorage.getItem(ATTRIBUTION_KEYS.INITIAL_REFERRER) ?? null;
        }

        // Build a safe landing URL using an allowlist of known marketing params only.
        // This prevents internal/sensitive params (e.g. code, state, link_id, cko-payment-id,
        // cko-session-id) from being persisted into attribution history or sent to the backend.
        // Both /oauth-web/ and legacy /oauth/ callback paths are excluded entirely.
        const ALLOWED_UTM_PARAMS = new Set([
            'utm_source',
            'utm_medium',
            'utm_campaign',
            'utm_content',
            'utm_term',
            'fbclid',
            'gclid',
            'msclkid',
            'dclid',
            'rdt_cid',
            'ttclid',
            'ScCid',
            'sccid',
            'twclid',
            'sclid',
        ]);
        const safeLandingUrl = (() => {
            if (utm.utm_landing) return utm.utm_landing;
            if (typeof window === 'undefined') return undefined;
            if (/\/oauth(?:-web)?\/[^/]+\/callback/.test(window.location.pathname)) return undefined;
            try {
                const url = new URL(window.location.href);
                const filteredParams = new URLSearchParams();
                url.searchParams.forEach((value, key) => {
                    if (ALLOWED_UTM_PARAMS.has(key)) {
                        filteredParams.set(key, value);
                    }
                });
                const qs = filteredParams.toString();
                return qs ? `${url.origin}${url.pathname}?${qs}` : `${url.origin}${url.pathname}`;
            } catch {
                return window.location.pathname;
            }
        })();

        // Create new entry with enriched data
        const newEntry: AttributionHistoryEntry = {
            ...utm,
            utm_landing: safeLandingUrl,
            timestamp: new Date().toISOString(),
            event_id: uuidv4(),
            gclid,
            fbclid,
            msclkid,
            dclid,
            rdt_cid,
            ttclid,
            twclid,
            sc_cid,
            cid,
            anonymous_id,
            initial_referrer,
        };

        // Add the entry using the utility function (handles deduplication)
        const addedNewTouchpoint = addAttributionEntry(newEntry);

        // If user is logged in and this touchpoint has UTM campaign data or click IDs,
        // send it immediately to the server. Click-ID-only arrivals (no UTM params) must
        // also be flushed in real time so they are not silently dropped.
        const { user, userId } = useAuthStore.getState();
        const canPostAttributionTouchpoint = isWithinAttributionTouchpointPostWindow(user?.created_ts);

        if (addedNewTouchpoint && userId && canPostAttributionTouchpoint && (hasCampaignData || hasClickIds)) {
            await interactionResponse();
            addUserAttributionTouchpoint(userId, newEntry, undefined, (err) => {
                console.warn('Failed to send attribution touchpoint:', err);
            });
        }
    } catch (e) {
        console.error('Failed to track UTM history', e);
    }
}

export const useAttributionStore = create<AttributionState>()((set) => ({
    utmData: defaultUTM,
    source: undefined,
    actions: {
        setUTMData: (utm) => {
            set({ utmData: utm });
            void trackUtmHistory(utm);
        },
        setSource: (source) => set({ source }),
    },
}));
